import useSWR from "swr";
import { ComponentRef, useRef, useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import {
  ArrowLeftIcon,
  DownloadIcon,
  EllipsisVerticalIcon,
  MessageSquareIcon,
  MessageSquarePlusIcon,
  TrashIcon
} from "lucide-react";
import { Chat } from "@/components/ai/Chat";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ConversationEmptyState } from "@/components/ui/ai/conversation";
import { ScrollLock } from "@/components/ScrollLock";
import { cn, fetcher, isProd, okstatus } from "@/lib/utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { ThreadPreview } from "@/pages/api/ai/chat";
import { JSONSafe } from "@/types";
import { formatMarkdown } from "@/components/formatMarkdown";
import { Badge } from "@/components/ui/badge";

// Extract first bold line, heading, or paragraph for use as a preview
const simplifyMarkdown = (markdown: string) => {
  let str = markdown.startsWith("#")
    ? markdown.substring(markdown.indexOf(" ")).trim().split("\n")[0]
    : markdown.startsWith("**")
      ? markdown
          .substring(2, 2 + markdown.substring(2).indexOf("**"))
          .trim()
          .split("\n")[0]
      : markdown.split("\n")[0];
  if (str.startsWith("Title: ")) {
    str = str.substring(7).trim();
  }
  return str;
};

export const Chats = ({
  threadId,
  onNewThread,
  onBack
}: {
  threadId?: string;
  onNewThread?: () => void;
  onBack?: () => void;
}) => {
  const router = useRouter();
  const { data: threads, mutate } = useSWR<JSONSafe<ThreadPreview>[]>("/api/ai/chat", fetcher);
  const deleteRef = useRef<ComponentRef<typeof ConfirmDialog>>(null);
  const [deleteThreadId, setDeleteThreadId] = useState<string | null>(null);

  const handleDeleteThread = () => {
    if (!deleteThreadId) {
      return;
    }
    fetch(`/api/ai/chat/${deleteThreadId}`, { method: "DELETE" })
      .then(okstatus)
      .then(() => {
        if (threadId === deleteThreadId) {
          router.push("/chat");
        }
        mutate();
      });
  };

  const handleExportJson = (thread: JSONSafe<ThreadPreview>) => {
    fetch(`/api/ai/chat/${thread.id}`)
      .then(okstatus)
      .then(res => res.json())
      .then(messages => {
        const blob = new Blob([JSON.stringify(messages, null, 2)], {
          type: "application/json"
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dollardeploy-ai-${isProd() ? "" : "dev-"}${thread.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <ScrollLock className="flex h-[calc(100vh-3.5rem)] overflow-hidden">
      {/* Thread list: visible when no thread or viewport ≥lg; hidden when thread open and <lg */}
      <div
        className={cn(
          "flex flex-col border-r bg-sidebar w-full lg:w-72 shrink-0",
          threadId ? "hidden lg:flex" : "flex"
        )}
      >
        <div className="p-3 border-b">
          <Button
            onClick={onNewThread}
            className="w-full"
            variant="outline"
            aria-label="Start new chat"
            disabled={!onNewThread}
          >
            <MessageSquarePlusIcon className="mr-2 size-4" />
            New Chat
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto touch-auto overscroll-contain">
          {threads?.length ? (
            <div className="flex flex-col gap-0.5 p-1">
              {threads.map(thread => (
                <div
                  key={thread.id}
                  className={cn(
                    "group flex items-start rounded-md border border-transparent transition-all",
                    threadId === thread.id
                      ? "border-border bg-card"
                      : "hover:border-border hover:bg-sidebar-accent"
                  )}
                >
                  <Link
                    href={`/chat/${thread.id}`}
                    className="flex-1 flex-col min-w-0 p-2"
                    aria-label={`Open thread: ${thread.preview || "New conversation"}`}
                  >
                    <div className="flex items-start gap-1.5">
                      <MessageSquareIcon className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="text-sm truncate text-foreground">
                        {formatMarkdown(
                          simplifyMarkdown(thread.preview) || "New conversation"
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6 relative">
                      <span className="">
                        {thread.messageCount}{" "}
                        {thread.messageCount === 1 ? "message" : "messages"} ·{" "}
                      </span>
                      <span>{formatDistanceToNowStrict(new Date(thread.updatedAt))} ago</span>
                      <span className="hidden group-hover:block absolute right-[-1rem] top-0 text-xs">
                        {thread.inputCost !== undefined
                          ? "$" +
                            (
                              (thread.inputCost ?? 0) +
                              (thread.outputCost ?? 0) +
                              (thread.reasoningCost ?? 0)
                            ).toFixed(2)
                          : ""}
                      </span>
                    </p>
                    {thread.tags?.length ? (
                      <div className="text-xs text-muted-foreground ml-5 mt-1 relative gap-1 inline-flex">
                        {thread.tags.map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </Link>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 mt-2 mr-1 size-7"
                        aria-label="Thread actions"
                      >
                        <EllipsisVerticalIcon className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleExportJson(thread)}>
                        <DownloadIcon className="size-4 mr-2" />
                        Export JSON
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setDeleteThreadId(thread.id);
                          deleteRef.current?.open();
                        }}
                      >
                        <TrashIcon className="size-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          ) : (
            <p className="p-4 text-center text-muted-foreground text-sm">
              No conversations yet
            </p>
          )}
        </div>
      </div>

      {/* Main panel: when no thread, empty state visible only on lg and up */}
      <div
        className={cn(
          "flex-1 flex flex-col min-w-0 min-h-0 touch-auto overscroll-contain",
          threadId ? "flex" : "hidden lg:flex"
        )}
      >
        {/* Back to list: shown when viewport < lg */}
        {threadId && (
          <div className="lg:hidden flex items-center gap-2 p-2 border-b bg-background shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              disabled={!onBack}
              aria-label="Back to thread list"
            >
              <ArrowLeftIcon className="size-4" />
            </Button>
          </div>
        )}

        {threadId ? (
          <Chat id={threadId} onMessageSent={mutate} />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <ConversationEmptyState
              icon={<MessageSquareIcon className="size-10" />}
              title="Welcome to Chat"
              description="Start a new conversation or pick one from the sidebar"
            />

            <Button onClick={onNewThread} className="mt-6" disabled={!onNewThread}>
              <MessageSquarePlusIcon className="mr-2 size-4" />
              Start a New Chat
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        ref={deleteRef}
        title="Delete conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
        continueLabel="Delete"
        continueVariant="destructive"
        onContinue={handleDeleteThread}
      />
    </ScrollLock>
  );
};
